import { useEffect, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { AuthInfoScreen } from "@/components/auth/AuthInfoScreen";

interface EmailChangeInfoProps {
  navigate: (page: string) => void;
}

interface EmailChangeInfoState {
  email: string;
  verificationRequired: boolean;
}

const EMAIL_CHANGE_INFO_KEY = "emailChangeInfo";

export default function EmailChangeInfo({ navigate }: EmailChangeInfoProps) {
  const { t } = useApp();
  const [info, setInfo] = useState<EmailChangeInfoState | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const raw = sessionStorage.getItem(EMAIL_CHANGE_INFO_KEY);
    if (!raw) {
      navigate("login");
      return;
    }
    try {
      const parsed = JSON.parse(raw) as EmailChangeInfoState;
      if (
        !parsed ||
        typeof parsed.email !== "string" ||
        typeof parsed.verificationRequired !== "boolean"
      ) {
        navigate("login");
        return;
      }
      setInfo(parsed);
    } catch {
      navigate("login");
      return;
    } finally {
      sessionStorage.removeItem(EMAIL_CHANGE_INFO_KEY);
    }
  }, [navigate]);

  if (!info) {
    return null;
  }

  const email = info.email?.trim();
  const needsVerification = info.verificationRequired;

  const title = needsVerification
    ? t("auth.verifyEmailTitle")
    : t("account.emailChangeCompleteTitle");
  const subtitle = needsVerification
    ? t("account.emailChangeVerifySubtitle")
    : t("account.emailChangeCompleteSubtitle");
  const body = needsVerification
    ? t("account.emailChangeVerifyBody").replace(
        "{{email}}",
        email || t("auth.emailPlaceholder")
      )
    : t("auth.registrationCompleteBody");
  const nextStep = needsVerification
    ? t("auth.verifyEmailNextStep")
    : t("account.emailChangeCompleteNextStep");

  return (
    <AuthInfoScreen
      title={title}
      subtitle={subtitle}
      body={body}
      nextStep={nextStep}
      primaryActionLabel={t("auth.login")}
      onPrimaryAction={() => navigate("login")}
      secondaryActionLabel={t("common.back")}
      onSecondaryAction={() => navigate("home")}
    />
  );
}
